import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { REGION_LIST } from "@/lib/countries";
import { Search, MapPin, Package } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import TierBadge from "@/components/TierBadge";

export default function SupplierDirectory() {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [items, setItems] = useState([]);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (region) params.region = region;
    const { data } = await api.get("/suppliers", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [region]);
  const search = (e) => { e.preventDefault(); load(); };

  return (
    <div data-testid="suppliers-directory" className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-3xl">
          <div className="overline text-primary mb-4">DIRECTORY</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">Verified suppliers across Asia.</h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Every supplier below has been onboarded, vetted and compliance-checked by Safeway Finland. Browse storefronts, view certifications, and message them directly.
          </p>
        </div>

        <form onSubmit={search} className="mt-10 bg-white border border-border rounded-lg p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="suppliers-search" className="pl-9" placeholder="Search company or name…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
          <select data-testid="suppliers-region" className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">All regions</option>
            {REGION_LIST.map((r) => <option key={r}>{r}</option>)}
          </select>
          <Button type="submit" data-testid="suppliers-search-btn">Search</Button>
        </form>

        {items.length === 0 ? (
          <Card className="mt-8"><CardContent className="py-16 text-center text-sm text-muted-foreground" data-testid="suppliers-empty">No suppliers match your search yet.</CardContent></Card>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((s) => {
              const initials = (s.company_name || s.name).split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
              return (
                <Link to={`/suppliers/${s.id}`} key={s.id} className="bg-white border border-border rounded-lg p-6 card-lift block" data-testid={`supplier-card-${s.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-lg bg-secondary text-primary grid place-items-center font-heading font-black text-lg">{initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-heading font-bold text-secondary text-lg leading-tight truncate">{s.company_name || s.name}</div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.country || "—"}</div>
                      <div className="mt-2"><TierBadge tier={s.tier} /></div>
                    </div>
                  </div>
                  <div className="mt-4"><RatingStars value={s.rating?.avg || 0} count={s.rating?.count || 0} /></div>
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <Badge variant="outline" className="text-xs"><Package className="w-3 h-3 mr-1" />{s.product_count} product{s.product_count === 1 ? "" : "s"}</Badge>
                    <span className="text-xs text-primary font-medium">View storefront →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
